// Habits and completions of the signed-in user, kept in memory and cached in
// localStorage (instant start, works offline).
//
// Check-offs are optimistic: they change the state at once and go into an
// outbox that is sent in order; without connection it waits for the next
// chance (online event, focus, retry timer). Setting a completion is
// idempotent, so replaying is safe. Server data fetched later is overlaid with
// the ops still waiting. Changes to habits themselves need a connection.
import { useSyncExternalStore } from 'react';
import type { DataResponse, Habit, HabitInput, SetCompletionRequest, UpdateHabitRequest } from '../../shared/api';
import { todayKey, type DayKey } from '../../shared/days';
import { ApiRequestError, api } from './api';
import { readJson, removePrefix, writeJson } from './storage';

export type DoneMap = ReadonlyMap<string, ReadonlySet<DayKey>>;
export type CountMap = ReadonlyMap<string, ReadonlyMap<DayKey, number>>;

export interface StoreState {
  habits: readonly Habit[];
  done: DoneMap;
  counts: CountMap;
  /** Data is available (from the cache or the server). */
  loaded: boolean;
  /** The last request failed for lack of connection. */
  offline: boolean;
  /** Check-offs not yet saved on the server. */
  pending: number;
  /** First load failed and there is no cache. */
  error: string | null;
}

type Op = SetCompletionRequest;
type CachedOp = { habitId: string; day: DayKey; count?: number; done?: boolean };

const EMPTY: StoreState = { habits: [], done: new Map(), counts: new Map(), loaded: false, offline: false, pending: 0, error: null };
const PREFIX = 'ph:u:';
const EMPTY_DAYS: ReadonlySet<DayKey> = new Set();

let state: StoreState = EMPTY;
let userId: string | null = null;
let outbox: Op[] = [];
let flushing = false;
let retryTimer: number | undefined;
let lastSync = 0;
let saveTimer: number | undefined;
const listeners = new Set<() => void>();

const cacheKey = () => `${PREFIX}${userId}:data`;
const outboxKey = () => `${PREFIX}${userId}:outbox`;

function readOutbox(): Op[] {
  return (readJson<CachedOp[]>(outboxKey()) ?? []).map(({ habitId, day, count, done }) => ({
    habitId,
    day,
    count: count ?? (done ? 1 : 0),
  }));
}

function setState(patch: Partial<StoreState>): void {
  state = { ...state, ...patch };
  for (const listener of listeners) listener();
}

function persist(): void {
  if (!userId) return;
  window.clearTimeout(saveTimer);
  saveTimer = window.setTimeout(() => {
    if (!userId || !state.loaded) return;
    writeJson(cacheKey(), toResponse(state));
  }, 250);
}

function persistOutbox(): void {
  if (userId) writeJson(outboxKey(), outbox);
  setState({ pending: outbox.length });
}

function toResponse(s: Pick<StoreState, 'habits' | 'done' | 'counts'>): DataResponse {
  const completions: Record<string, DayKey[]> = {};
  const completionCounts: Record<string, Record<string, number>> = {};
  for (const habit of s.habits) {
    completions[habit.id] = [...(s.done.get(habit.id) ?? [])].sort();
    completionCounts[habit.id] = Object.fromEntries(s.counts.get(habit.id) ?? []);
  }
  return { habits: [...s.habits], completions, completionCounts };
}

function fromResponse(data: DataResponse): Pick<StoreState, 'habits' | 'done' | 'counts'> {
  const counts = new Map<string, Map<DayKey, number>>();
  const done = new Map<string, Set<DayKey>>();
  for (const habit of data.habits) {
    const perDay = new Map<DayKey, number>();
    const incoming = data.completionCounts?.[habit.id];
    if (incoming) for (const [day, count] of Object.entries(incoming)) perDay.set(day as DayKey, count);
    else for (const day of data.completions[habit.id] ?? []) perDay.set(day, 1);
    counts.set(habit.id, perDay);
    const target = habit.schedule.kind === 'daily' ? habit.schedule.times ?? 1 : 1;
    done.set(habit.id, new Set([...perDay].filter(([, count]) => count >= target).map(([day]) => day)));
  }
  for (const op of outbox) applyOp(done, counts, op);
  for (const habit of data.habits) {
    const target = habit.schedule.kind === 'daily' ? habit.schedule.times ?? 1 : 1;
    done.set(habit.id, new Set([...(counts.get(habit.id) ?? [])].filter(([, count]) => count >= target).map(([day]) => day)));
  }
  return { habits: sortHabits(data.habits), done, counts };
}

function applyOp(done: Map<string, Set<DayKey>>, counts: Map<string, Map<DayKey, number>>, op: Op): void {
  const days = done.get(op.habitId);
  const perDay = counts.get(op.habitId);
  if (!days || !perDay) return;
  if (op.count > 0) { days.add(op.day); perDay.set(op.day, op.count); }
  else { days.delete(op.day); perDay.delete(op.day); }
}

const sortHabits = (habits: readonly Habit[]) => [...habits].sort((a, b) => a.position - b.position);

// ---------------------------------------------------------------------------
// Lifecycle

/** Starts the store for a user: cached data first, then the server. */
export function startStore(id: string): void {
  if (userId === id) return;
  userId = id;
  outbox = readOutbox();
  const cached = readJson<DataResponse>(cacheKey());
  state = cached ? { ...EMPTY, ...fromResponse(cached), loaded: true, pending: outbox.length } : { ...EMPTY, pending: outbox.length };
  for (const listener of listeners) listener();
  void sync();
}

/** Signed out: forget everything of this user on this device. */
export function stopStore(): void {
  window.clearTimeout(retryTimer);
  window.clearTimeout(saveTimer);
  removePrefix(PREFIX);
  userId = null;
  outbox = [];
  state = EMPTY;
  for (const listener of listeners) listener();
}

/** Sends waiting check-offs, then loads the current data. */
export async function sync(): Promise<void> {
  const id = userId;
  if (!id) return;
  await flush();
  if (outbox.length > 0 || userId !== id) return; // still offline: keep the local state
  try {
    const data = await api.data();
    if (userId !== id) return;
    lastSync = Date.now();
    setState({ ...fromResponse(data), loaded: true, offline: false, error: null });
    persist();
  } catch (error) {
    if (userId !== id) return;
    const offline = error instanceof ApiRequestError && error.transient;
    setState({
      offline,
      error: state.loaded ? null : offline ? 'Keine Verbindung. Deine Habits erscheinen, sobald du online bist.' : 'Deine Daten konnten nicht geladen werden.',
    });
    if (offline) scheduleRetry();
  }
}

function scheduleRetry(): void {
  window.clearTimeout(retryTimer);
  retryTimer = window.setTimeout(() => void sync(), 15_000);
}

window.addEventListener('online', () => void sync());
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState !== 'visible' || !userId) return;
  if (outbox.length > 0 || Date.now() - lastSync > 30_000) void sync();
});

async function flush(): Promise<void> {
  if (flushing) return;
  flushing = true;
  let reload = false;
  try {
    while (outbox.length > 0 && userId) {
      const op = outbox[0]!;
      try {
        await api.setCompletion(op);
        outbox.shift();
        persistOutbox();
        if (state.offline) setState({ offline: false });
      } catch (error) {
        if (error instanceof ApiRequestError && (error.transient || error.status === 401)) {
          if (error.transient) {
            setState({ offline: error.code === 'network' });
            scheduleRetry();
          }
          return;
        }
        // Rejected (habit deleted elsewhere, invalid day): drop it, reload the truth.
        outbox.shift();
        persistOutbox();
        reload = true;
      }
    }
  } finally {
    flushing = false;
  }
  if (reload) void sync();
}

// ---------------------------------------------------------------------------
// Reading

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useStore(): StoreState {
  return useSyncExternalStore(subscribe, () => state);
}

export function daysOf(done: DoneMap, habitId: string): ReadonlySet<DayKey> {
  return done.get(habitId) ?? EMPTY_DAYS;
}

export function countOf(counts: CountMap, habitId: string, day: DayKey): number {
  return counts.get(habitId)?.get(day) ?? 0;
}

// ---------------------------------------------------------------------------
// Changes

export function setDone(habitId: string, day: DayKey, value: boolean): void {
  const habit = state.habits.find((item) => item.id === habitId);
  const target = habit?.schedule.kind === 'daily' ? habit.schedule.times ?? 1 : 1;
  setCount(habitId, day, value ? target : 0);
}

export function setCount(habitId: string, day: DayKey, count: number): void {
  const days = new Set(state.done.get(habitId) ?? []);
  const perDay = new Map(state.counts.get(habitId) ?? []);
  if (count === (perDay.get(day) ?? 0)) return;
  const habit = state.habits.find((item) => item.id === habitId);
  const target = habit?.schedule.kind === 'daily' ? habit.schedule.times ?? 1 : 1;
  if (count >= target) days.add(day); else days.delete(day);
  if (count > 0) perDay.set(day, count);
  else { days.delete(day); perDay.delete(day); }
  const done = new Map(state.done);
  done.set(habitId, days);
  const counts = new Map(state.counts);
  counts.set(habitId, perDay);
  // A later op for the same day replaces a waiting one (never the one in flight).
  outbox = outbox.filter((op, i) => (flushing && i === 0) || op.habitId !== habitId || op.day !== day);
  outbox.push({ habitId, day, count });
  setState({ done, counts });
  persistOutbox();
  persist();
  void flush();
}

export async function createHabit(input: HabitInput): Promise<Habit> {
  const habit = await api.createHabit({ ...input, today: todayKey() });
  const done = new Map(state.done);
  done.set(habit.id, new Set());
  const counts = new Map(state.counts);
  counts.set(habit.id, new Map());
  setState({ habits: sortHabits([...state.habits, habit]), done, counts });
  persist();
  return habit;
}

export async function updateHabit(id: string, patch: UpdateHabitRequest): Promise<Habit> {
  const habit = await api.updateHabit(id, patch);
  setState({ habits: sortHabits(state.habits.map((h) => (h.id === id ? habit : h))) });
  persist();
  return habit;
}

export async function deleteHabit(id: string): Promise<void> {
  await api.deleteHabit(id);
  const done = new Map(state.done);
  done.delete(id);
  const counts = new Map(state.counts);
  counts.delete(id);
  outbox = outbox.filter((op, i) => (flushing && i === 0) || op.habitId !== id);
  persistOutbox();
  setState({ habits: state.habits.filter((h) => h.id !== id), done, counts });
  persist();
}

/** New order of the given habits (optimistic); positions of the others stay. */
export async function reorderHabits(ids: string[]): Promise<void> {
  const previous = state.habits;
  const rank = new Map(ids.map((id, i) => [id, i]));
  const others = previous.filter((h) => !rank.has(h.id));
  const ordered = [...ids.map((id) => previous.find((h) => h.id === id)!).filter(Boolean), ...others];
  setState({ habits: ordered.map((h, position) => ({ ...h, position })) });
  persist();
  try {
    await api.reorder(ordered.map((h) => h.id));
  } catch (error) {
    setState({ habits: previous });
    persist();
    throw error;
  }
}
